import React, { useRef, useEffect } from 'react';
import { getResponsiveMultiplier } from '../../utils/visualizerUtils';

// Preload the city image once
let cityImage = null;
function getCityImage() {
  if (!cityImage) {
    cityImage = new window.Image();
    // Use relative path for Electron builds (when base is relative), absolute for web
    const isElectron = import.meta.env.VITE_ELECTRON === 'true' || window.location.protocol === 'file:';
    cityImage.src = isElectron ? './visualizers/future-city.jpg' : '/visualizers/future-city.jpg';
  }
  return cityImage;
}

// City Warp Visualizer
export function drawCityWarp(ctx, dataArray, width, height, time, isPlaying) {
  if (!ctx || !dataArray || width <= 0 || height <= 0) return;

  // --- 1. Draw the city background image, autoscaled ---
  const img = getCityImage();
  if (img.complete && img.naturalWidth > 0) {
    // Autoscale to cover canvas (center-crop)
    const imgAspect = img.naturalWidth / img.naturalHeight;
    const canvasAspect = width / height;
    let drawWidth, drawHeight, dx, dy;
    if (canvasAspect > imgAspect) {
      drawWidth = width;
      drawHeight = width / imgAspect;
      dx = 0;
      dy = (height - drawHeight) / 2;
    } else {
      drawHeight = height;
      drawWidth = height * imgAspect;
      dx = (width - drawWidth) / 2;
      dy = 0;
    }
    ctx.drawImage(img, dx, dy, drawWidth, drawHeight);
  } else {
    // Fallback: dark gradient
    const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
    bgGradient.addColorStop(0, 'hsla(240, 80%, 18%, 0.7)');
    bgGradient.addColorStop(1, 'hsla(260, 100%, 8%, 0.9)');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, width, height);
  }

  // --- 1.5. Subtle fog layer (layered, 3D effect, animated) ---
  // Use multiple translucent white gradients for depth, with subtle slow animation
  for (let f = 0; f < 3; f++) {
    // Animate horizontal drift and vertical undulation per layer
    const fogDrift = Math.sin(time * 0.04 + f * 1.2) * 24 * (1 + f * 0.3); // slow, subtle
    const fogWave = Math.sin(time * 0.07 + f * 2.1) * 10 * (1 + f * 0.2);
    const fogY = height * (0.55 + f * 0.12) + fogWave;
    const fogHeight = height * (0.13 + f * 0.07);
    const fogGradient = ctx.createLinearGradient(fogDrift, fogY, fogDrift, fogY + fogHeight);
    fogGradient.addColorStop(0, `rgba(255,255,255,${0.08 - f * 0.02})`);
    fogGradient.addColorStop(0.5, `rgba(255,255,255,${0.13 - f * 0.03})`);
    fogGradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.save();
    ctx.globalAlpha = 0.7 - f * 0.18;
    ctx.fillStyle = fogGradient;
    ctx.fillRect(0, fogY, width, fogHeight);
    ctx.restore();
  }

  // --- 2. Animated stars ---
  const starSeed = 42;
  function seededRandom(seed) {
    let x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  }
  for (let i = 0; i < 48; i++) {
    const sx = width * seededRandom(i * 13.13 + starSeed);
    const sy = height * 0.08 + height * 0.35 * seededRandom(i * 7.77 + starSeed + 1);
    const size = 0.8 + 1.8 * seededRandom(i * 3.33 + starSeed + 2);
    const twinkle = 0.5 + 0.5 * Math.abs(Math.sin(time * 0.7 + i * 1.3));
    ctx.save();
    ctx.globalAlpha = 0.10 + 0.25 * twinkle;
    ctx.fillStyle = `hsl(${215 + i * 3}, 100%, 85%)`;
    ctx.beginPath();
    ctx.arc(sx, sy, size, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // --- 2.5. Animated twinkling city lights (sparse, random, fade in/out) ---
  const cityLightCount = 22;
  for (let i = 0; i < cityLightCount; i++) {
    // Random position in lower half of canvas, but above spectrum bars
    const lx = width * seededRandom(i * 5.1 + 99);
    const ly = height * (0.55 + 0.35 * seededRandom(i * 3.3 + 321));
    // Random color: mostly warm, some cool
    const hue = seededRandom(i * 8.8 + 123) > 0.7 ? 200 + 40 * seededRandom(i * 2.2 + 456) : 45 + 20 * seededRandom(i * 4.4 + 555);
    const sat = 80 + 20 * seededRandom(i * 6.6 + 789);
    const light = 60 + 30 * seededRandom(i * 7.7 + 888);
    // Twinkle: fade in/out, each with unique speed/phase
    const twinkleSpeed = 0.7 + 0.8 * seededRandom(i * 7.7 + 888);
    const twinklePhase = seededRandom(i * 9.9 + 777) * Math.PI * 2;
    const twinkle = 0.5 + 0.5 * Math.sin(time * twinkleSpeed + twinklePhase);
    ctx.save();
    ctx.globalAlpha = 0.18 + 0.55 * Math.max(0, twinkle); // Only positive phase
    ctx.beginPath();
    ctx.arc(lx, ly, 2.2 + 1.5 * seededRandom(i * 2.1 + 222), 0, Math.PI * 2);
    ctx.fillStyle = `hsl(${hue}, ${sat}%, ${light}%)`;
    ctx.shadowColor = `hsl(${hue}, 100%, 85%)`;
    ctx.shadowBlur = 8 + 8 * seededRandom(i * 1.1 + 333);
    ctx.fill();
    ctx.restore();
  }

  // --- 3. Animated window lights ---
  const windowCount = 38;
  for (let i = 0; i < windowCount; i++) {
    const wx = width * seededRandom(i * 5.1 + 99);
    const wy = height * (0.55 + 0.35 * seededRandom(i * 3.3 + 321));
    const twinkleSpeed = 0.5 + 0.7 * seededRandom(i * 7.7 + 888);
    const twinklePhase = seededRandom(i * 9.9 + 777) * Math.PI * 2;
    const twinkle = 0.5 + 0.5 * Math.abs(Math.sin(time * twinkleSpeed + twinklePhase));
    ctx.save();
    ctx.globalAlpha = 0.45 + 0.45 * twinkle;
    ctx.fillStyle = `hsl(${55 + (i % 2) * 10}, 100%, ${65 + 20 * seededRandom(i * 4.4 + 555)}%)`;
    ctx.fillRect(wx, wy, 6, 6);
    ctx.restore();
  }

  // --- 4. Spaceships flying by ---
  for (let s = 0; s < 2; s++) {
    const shipY = height * 0.22 + s * 22 + Math.sin(time * 0.7 + s) * 8;
    // Reverse direction: ships fly left-to-right
    const shipX = -100 + ((time * 80 + s * 200) % (width + 200));
    ctx.save();
    ctx.globalAlpha = 0.98;
    // --- Cyberpunk ship, more detailed and angular ---
    // 1. Engine trail (neon)
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.beginPath();
    ctx.moveTo(shipX + 2, shipY + 4);
    ctx.lineTo(shipX - 18, shipY + 4 + Math.sin(time * 2 + s) * 2);
    ctx.lineWidth = 7;
    ctx.strokeStyle = s === 0 ? 'rgba(0,255,255,0.7)' : 'rgba(255,0,255,0.6)';
    ctx.shadowColor = s === 0 ? '#0ff' : '#f0f';
    ctx.shadowBlur = 18;
    ctx.stroke();
    ctx.restore();
    // 2. Main body (angular, layered)
    ctx.beginPath();
    ctx.moveTo(shipX + 2, shipY + 4); // engine
    ctx.lineTo(shipX + 10, shipY - 2); // left fin
    ctx.lineTo(shipX + 28, shipY - 2); // nose top
    ctx.lineTo(shipX + 34, shipY + 4); // nose tip
    ctx.lineTo(shipX + 28, shipY + 10); // nose bottom
    ctx.lineTo(shipX + 10, shipY + 10); // right fin
    ctx.closePath();
    ctx.fillStyle = s === 0 ? 'rgba(0,255,255,0.92)' : 'rgba(255,0,255,0.85)';
    ctx.shadowColor = s === 0 ? '#0ff' : '#f0f';
    ctx.shadowBlur = 10;
    ctx.fill();
    ctx.shadowBlur = 0;
    // 3. Cockpit (bubble, offset)
    ctx.beginPath();
    ctx.ellipse(shipX + 24, shipY + 4, 4, 2.5, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 6;
    ctx.fill();
    ctx.shadowBlur = 0;
    // 4. Neon stripes (top and bottom)
    ctx.save();
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    ctx.moveTo(shipX + 8, shipY + 2);
    ctx.lineTo(shipX + 28, shipY + 2);
    ctx.moveTo(shipX + 8, shipY + 6);
    ctx.lineTo(shipX + 28, shipY + 6);
    ctx.strokeStyle = s === 0 ? '#0ff' : '#f0f';
    ctx.lineWidth = 1.7;
    ctx.shadowColor = s === 0 ? '#0ff' : '#f0f';
    ctx.shadowBlur = 8;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
    // 5. Fins (side, top, bottom)
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(shipX + 10, shipY + 1);
    ctx.lineTo(shipX + 2, shipY - 6);
    ctx.lineTo(shipX + 12, shipY + 4);
    ctx.closePath();
    ctx.fillStyle = s === 0 ? 'rgba(0,255,255,0.18)' : 'rgba(255,0,255,0.13)';
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(shipX + 10, shipY + 7);
    ctx.lineTo(shipX + 2, shipY + 14);
    ctx.lineTo(shipX + 12, shipY + 4);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    // 6. Top fin
    ctx.save();
    ctx.globalAlpha = 0.4;
    ctx.beginPath();
    ctx.moveTo(shipX + 18, shipY + 1);
    ctx.lineTo(shipX + 18, shipY - 9);
    ctx.lineTo(shipX + 20, shipY + 1);
    ctx.closePath();
    ctx.fillStyle = s === 0 ? 'rgba(0,255,255,0.18)' : 'rgba(255,0,255,0.13)';
    ctx.fill();
    ctx.restore();
    // 7. Underglow
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.beginPath();
    ctx.ellipse(shipX + 18, shipY + 12, 16, 4, 0, 0, Math.PI * 2);
    ctx.fillStyle = s === 0 ? 'rgba(0,255,255,0.5)' : 'rgba(255,0,255,0.4)';
    ctx.shadowColor = s === 0 ? '#0ff' : '#f0f';
    ctx.shadowBlur = 10;
    ctx.fill();
    ctx.restore();
    ctx.restore();
  }

  // --- 5. Animated spectrum bars at the bottom (optimized for performance) ---
  if (isPlaying && dataArray && dataArray.length > 0) {
    const maxBars = Math.min(160, dataArray.length); // Increased from 80 to 160 (37% reduction)
    const step = Math.floor(dataArray.length / maxBars);
    const barWidth = width / maxBars * getResponsiveMultiplier();
    let x = 0;
    for (let i = 0; i < maxBars; i++) {
      const dataIndex = Math.min(i * step, dataArray.length - 1);
      const intensity = dataArray[dataIndex] / 255;
      const barHeight = Math.max(10, intensity * height * 0.45 + Math.sin(time * 2 + dataIndex * 0.15) * 10);
      const hue = 220 + (i / maxBars) * 80 + Math.sin(time + dataIndex) * 10;
      const gradient = ctx.createLinearGradient(0, height, 0, height - barHeight);
      gradient.addColorStop(0, `hsl(${hue}, 90%, 70%, 0.95)`);
      gradient.addColorStop(0.5, `hsl(${hue + 20}, 100%, 80%, 0.7)`);
      gradient.addColorStop(1, `hsl(${hue + 40}, 100%, 90%, 0.3)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(x, height - barHeight, barWidth - 1, barHeight);
      if (barHeight > 18) {
        ctx.save();
        ctx.globalAlpha = 0.18 + 0.12 * intensity;
        ctx.fillStyle = `hsl(${hue + 60}, 100%, 95%)`;
        ctx.fillRect(x, height - barHeight, barWidth - 1, 4);
        ctx.restore();
      }
      x += barWidth;
    }
  }

  // --- 6. Subtle bottom glow ---
  const glowGradient = ctx.createLinearGradient(0, height, 0, height - 60);
  glowGradient.addColorStop(0, 'hsla(260, 100%, 80%, 0.25)');
  glowGradient.addColorStop(1, 'hsla(260, 100%, 80%, 0)');
  ctx.fillStyle = glowGradient;
  ctx.fillRect(0, height - 60, width, 60);
}

function CityWarp({ ctx, dataArray, width, height, isPlaying }) {
  const time = Date.now() * 0.001;
  drawCityWarp(ctx, dataArray, width, height, time, isPlaying);
}

export default CityWarp;
export { CityWarp };
